import type { ForceVector } from '../scenes';

export interface VectorStyle {
  pixelsPerNewton: number;
  labelFont: string;
  showLabels: boolean;
  opacity: number;
  // Arrows longer than this (in pixels) are clipped so they don't run off-screen.
  // The arrow head is drawn at the cap and a small tick at the cap signals truncation.
  maxLength?: number;
}

const DEFAULT_MAX_LENGTH = 280;

export function drawVector(
  ctx: CanvasRenderingContext2D,
  v: ForceVector,
  label: string,
  style: VectorStyle,
): void {
  let dx = v.components.x * style.pixelsPerNewton;
  let dy = v.components.y * style.pixelsPerNewton;
  const rawLen = Math.hypot(dx, dy);
  if (rawLen < 1) return;

  const maxLen = style.maxLength ?? DEFAULT_MAX_LENGTH;
  const clipped = rawLen > maxLen;
  if (clipped) {
    const k = maxLen / rawLen;
    dx *= k;
    dy *= k;
  }

  const tipX = v.origin.x + dx;
  const tipY = v.origin.y + dy;
  const len = Math.hypot(dx, dy);

  ctx.save();
  ctx.globalAlpha = style.opacity;
  ctx.strokeStyle = v.color;
  ctx.fillStyle = v.color;
  ctx.lineWidth = 2;

  ctx.beginPath();
  ctx.moveTo(v.origin.x, v.origin.y);
  ctx.lineTo(tipX, tipY);
  ctx.stroke();

  const headLen = 8;
  const headAngle = Math.PI / 7;
  const angle = Math.atan2(dy, dx);
  ctx.beginPath();
  ctx.moveTo(tipX, tipY);
  ctx.lineTo(
    tipX - headLen * Math.cos(angle - headAngle),
    tipY - headLen * Math.sin(angle - headAngle),
  );
  ctx.lineTo(
    tipX - headLen * Math.cos(angle + headAngle),
    tipY - headLen * Math.sin(angle + headAngle),
  );
  ctx.closePath();
  ctx.fill();

  if (clipped) {
    // Small perpendicular tick across the tail near the cap to indicate clipping.
    const perpX = -dy / len;
    const perpY = dx / len;
    const tickHalf = 4;
    const tickX = v.origin.x + dx * 0.92;
    const tickY = v.origin.y + dy * 0.92;
    ctx.beginPath();
    ctx.moveTo(tickX + perpX * tickHalf, tickY + perpY * tickHalf);
    ctx.lineTo(tickX - perpX * tickHalf, tickY - perpY * tickHalf);
    ctx.stroke();
  }

  if (style.showLabels) {
    ctx.font = style.labelFont;
    ctx.fillText(label, tipX + 4, tipY - 4);
  }

  ctx.restore();
}
